/**
 * @file NordicLM20Upgrader.ts
 * @description N54LM20 固件升级器 (基于自定义 HID Feature Report 协议)
 *
 * 与同系列其他 Nordic 芯片（如 54 系列）走不同升级协议。
 * N54LM20 采用私有 HID Feature Report 命令（小包 + 双 ack 节流）：
 *   - 数据块 59 字节，跨块后间隔 100ms（双 ack）
 *   - 升级文件后缀 .bin
 *
 * 升级流程:
 *   Boot倒计时 → 文件信息(大小+CRC32) → 固件分片传输 → 重启
 *
 * 协议参数:
 *   - HID Feature Report ID: 0x09
 *   - 每包固定 64 字节 (SPEED_PACKET_SIZE)
 *   - 数据块大小 59 字节 (0x3B)
 *   - CRC32 校验
 *   - 轮询等待 ACK 确认
 */

import { BaseDevice } from '../../../../runtime/BaseDevice'
import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'

export class NordicLM20Upgrader {
  private device: BaseDevice
  private firmwareData: Uint8Array = new Uint8Array(0)
  private crcValue: number = 0

  private onProgressCallback: ((progress: number) => void) | null = null
  private onProgressFinishCallback: (() => void) | null = null

  private readonly REPORT_ID = 0x09
  private readonly SPEED_PACKET_SIZE = 64
  private readonly NORDIC_WRITE_LENGTH = 0x3b // 59 bytes per chunk

  constructor(device: BaseDevice) {
    this.device = device
  }

  // ==================== 公共方法 ====================

  setOnProgressCallback(callback: (progress: number) => void) {
    this.onProgressCallback = callback
  }

  setOnProgressFinishCallback(callback: () => void) {
    this.onProgressFinishCallback = callback
  }

  /**
   * 启动 N54LM20 固件升级
   * @param firmwareData .bin 固件数据
   */
  async startUpdate(firmwareData: Uint8Array): Promise<void> {
    this.firmwareData = firmwareData

    try {
      // 计算 CRC32
      this.crcValue = this.crc32(this.firmwareData)

      // 执行三阶段升级流程
      await this.writeCommondToBoot()
      await this.msleep(100)
      await this.writeSuitDataToDevice()
      await this.msleep(100)
      await this.writeCommondToResetDevice()
    } catch (error) {
      console.error('N54LM20 固件升级失败:', error)
      throw new DeviceError(
        DeviceErrorCode.WRITE_FAILED,
        `N54LM20 升级失败: ${error instanceof Error ? error.message : JSON.stringify(error)}`,
      )
    }
  }

  // ==================== 私有方法 ====================

  /**
   * CRC32 计算 (与 C++ FirmwareUpgradeNordic::Crc32 等价)
   *   - 初值 0xFFFFFFFE
   *   - 每字节按 LSB bit-by-bit 移位 + 条件 XOR poly(0xEDB88320)
   *   - 循环结束 ~CRC32 后返回
   */
  private crc32(data: Uint8Array): number {
    let crc = 0xfffffffe
    for (let i = 0; i < data.length; i++) {
      for (let j = 0; j < 8; j++) {
        if (crc & 1) {
          crc = (crc >>> 1) ^ 0xedb88320
        } else {
          crc = crc >>> 1
        }
        if (data[i] & (1 << j)) {
          crc ^= 0xedb88320
        }
      }
    }
    // return (~crc) >>> 0
    return ~crc >>> 0
  }

  /**
   * 发送 HID Feature Report 并轮询等待 ACK
   * WebHID sendFeatureReport(reportId, data) 会自动在 data 前面拼接 reportId，
   * 因此这里 slice(1) 去掉首字节 REPORT_ID，payload 63 字节 + 自动补 1 字节 = 设备收到 64 字节
   */
  private async sendCmd(buffer: Uint8Array): Promise<DataView> {
    // buffer[0] = REPORT_ID, strip it 后发送实际 payload (63 字节)
    const payload = buffer.slice(1)
    await this.device.channel.sendFeatureReport(this.REPORT_ID, payload)

    // 轮询等待响应
    const maxRetries = 6
    const timeoutMs = 3000
    let retries = 0
    const startTime = Date.now()
    await this.msleep(2)
    while (retries < maxRetries) {
      if (Date.now() - startTime > timeoutMs) {
        throw new DeviceError(DeviceErrorCode.TIMEOUT, 'LM20 sendCmd 超时')
      }
      try {
        const hexData = await this.device.channel.getFeatureReport(this.REPORT_ID)
        const ack = hexData.getUint8(0)
        if (ack === 0x02) {
          // 0x02 = 忙碌, 等20ms后重试
          await new Promise((r) => setTimeout(r, 20))
          retries++
          continue
        }
        return hexData
      } catch (error) {
        console.error('LM20 sendCmd error:', error)
        if (retries === maxRetries - 1) throw error
        retries++
      }
    }
    throw new DeviceError(DeviceErrorCode.TIMEOUT, 'LM20 达到最大重试次数')
  }

  /**
   * 创建 64 字节的命令缓冲区 (已填充0)
   * 格式: [REPORT_ID, 0x00, cmd, data, len, ...payload...]
   */
  private createBuffer(): Uint8Array {
    const buf = new Uint8Array(this.SPEED_PACKET_SIZE).fill(0)
    buf[0] = this.REPORT_ID
    buf[1] = 0
    return buf
  }

  private msleep(ms: number): Promise<void> {
    return new Promise((r) => setTimeout(r, ms))
  }

  private emitSendUpdateProgress(percent: number) {
    const p = Math.min(100, Math.max(0, Math.floor(percent * 100) / 100))
    if (this.onProgressCallback) {
      this.onProgressCallback(p)
    }
  }

  private emitUpdateFinished() {
    if (this.onProgressFinishCallback) {
      this.onProgressFinishCallback()
    }
  }

  // ==================== Phase 1: Boot ====================

  /**
   * 发送倒计时命令，使设备进入 Bootloader 模式
   */
  private async writeCommondToBoot(): Promise<void> {
    this.emitSendUpdateProgress(0)

    // 1a. 倒计时命令 3→2→1
    for (let a = 3; a > 0; a--) {
      const bootBuf = this.createBuffer()
      bootBuf[2] = 0 // cmd = 0
      bootBuf[3] = a // data = 倒计时值
      await this.sendCmd(bootBuf)
      await this.msleep(2)
    }

    // 1b. 准备命令 (cmd=0, data=7) x10
    for (let i = 0; i < 10; i++) {
      const bootBuf = this.createBuffer()
      bootBuf[2] = 0
      bootBuf[3] = 7
      await this.sendCmd(bootBuf)
      await this.msleep(2)
    }

    // 1c. 设置 cmd=6, data=7
    const preBuf = this.createBuffer()
    preBuf[2] = 6
    preBuf[3] = 7
    await this.sendCmd(preBuf)

    await this.msleep(2)

    // 1d. 完成准备 cmd=0, data=4
    const readyBuf = this.createBuffer()
    readyBuf[2] = 0
    readyBuf[3] = 4
    await this.sendCmd(readyBuf)
  }

  // ==================== Phase 2: 数据传输 ====================

  /**
   * 传输固件数据到设备
   */
  private async writeSuitDataToDevice(): Promise<void> {
    // 2a. 发送 cmd=5, data=7 (初始化数据传输)
    const initBuf = this.createBuffer()
    initBuf[2] = 5
    initBuf[3] = 7
    await this.sendCmd(initBuf)
    await this.msleep(1000)

    // 2b. 查询偏移量 cmd=3, data=7 (紧接 init, 无显式延时)
    const queryBuf = this.createBuffer()
    queryBuf[2] = 3
    queryBuf[3] = 7
    const responseData = await this.sendCmd(queryBuf)
    await this.msleep(30)

    // 从响应中读取 offsetSize (位置18-19, 小端序)
    const offsetSize = responseData.getUint8(18) | (responseData.getUint8(19) << 8)

    // 2c. 发送文件信息 cmd=1, data=6
    const infoBuf = this.createBuffer()
    infoBuf[2] = 1
    infoBuf[3] = 6
    infoBuf[4] = 0x0c

    // 文件总大小 (4字节, 小端序)
    infoBuf[5] = this.firmwareData.length & 0xff
    infoBuf[6] = (this.firmwareData.length >> 8) & 0xff
    infoBuf[7] = (this.firmwareData.length >> 16) & 0xff
    infoBuf[8] = (this.firmwareData.length >> 24) & 0xff

    // CRC32 值 (4字节, 小端序)
    infoBuf[9] = this.crcValue & 0xff
    infoBuf[10] = (this.crcValue >> 8) & 0xff
    infoBuf[11] = (this.crcValue >> 16) & 0xff
    infoBuf[12] = (this.crcValue >> 24) & 0xff

    await this.sendCmd(infoBuf)

    // 2d. 数据传输循环
    let dataLength = 0
    let count = 1
    let writeLength = 0

    while (dataLength < this.firmwareData.length) {
      const dataBuf = this.createBuffer()
      dataBuf[2] = 2 // cmd = 2
      dataBuf[3] = 6 // data = 6 (传输数据)

      if (dataLength + this.NORDIC_WRITE_LENGTH > this.firmwareData.length) {
        // 最后一段数据
        writeLength = this.firmwareData.length - dataLength
        dataBuf[4] = writeLength
        dataBuf.set(this.firmwareData.slice(dataLength, dataLength + writeLength), 5)
        await this.sendCmd(dataBuf)
        break
      }

      if (dataLength + this.NORDIC_WRITE_LENGTH > offsetSize * count) {
        // 跨块边界: 分两段发送
        writeLength = offsetSize * count - dataLength
        dataBuf[4] = writeLength
        dataBuf.set(this.firmwareData.slice(dataLength, dataLength + writeLength), 5)
        await this.sendCmd(dataBuf)
        count++

        // 中间确认 cmd=3, data=7 (x2, 间隔100ms, LM20 TIME_INTERVAL)
        const midBuf = this.createBuffer()
        midBuf[2] = 3
        midBuf[3] = 7
        await this.sendCmd(midBuf)
        await this.msleep(100)
        await this.sendCmd(midBuf)
      } else {
        // 正常数据块: NORDIC_WRITE_LENGTH (59) 字节
        writeLength = this.NORDIC_WRITE_LENGTH
        dataBuf[4] = writeLength
        dataBuf.set(this.firmwareData.slice(dataLength, dataLength + writeLength), 5)
        await this.sendCmd(dataBuf)
      }

      dataLength += writeLength
      this.emitSendUpdateProgress((dataLength * 100) / this.firmwareData.length)
    }

    this.emitSendUpdateProgress(100)
    this.emitUpdateFinished()
  }

  // ==================== Phase 3: 重启 ====================

  /**
   * 重启设备以应用新固件
   */
  private async writeCommondToResetDevice(): Promise<void> {
    const resetBuf1 = this.createBuffer()
    resetBuf1[2] = 3
    resetBuf1[3] = 7
    await this.sendCmd(resetBuf1)
    await this.msleep(1500)

    const resetBuf2 = this.createBuffer()
    resetBuf2[2] = 3
    resetBuf2[3] = 7
    await this.sendCmd(resetBuf2)
    await this.msleep(20)

    const resetBuf3 = this.createBuffer()
    resetBuf3[2] = 4
    resetBuf3[3] = 7
    await this.sendCmd(resetBuf3)
  }
}
